import type { ConnectionQuality } from '../net/connection';
import { GRENADES, type GrenadeId } from '../core/config';
import { type GrenadeInventory } from '../gameplay/grenade';
import { CrosshairRenderer } from '../ui/crosshair-settings';
import { buildKillFeedEntryHTML } from '../ui/weapon-icons';

/** Color map for grenade HUD icons. */
const GRENADE_HUD_COLORS: Record<string, string> = {
  he_grenade: '#cc3333',
  smoke_grenade: '#999999',
  flashbang: '#eeeeee',
  molotov: '#ff8833',
  incendiary: '#ff8833',
};

/** Short labels for grenade HUD. */
const GRENADE_LABELS: Record<string, string> = {
  he_grenade: 'HE',
  smoke_grenade: 'SM',
  flashbang: 'FL',
  molotov: 'ML',
  incendiary: 'IN',
};

export class HUD {
  private healthEl: HTMLElement;
  private armorEl: HTMLElement;
  private ammoCurrentEl: HTMLElement;
  private ammoReserveEl: HTMLElement;
  private weaponNameEl: HTMLElement;
  private timerEl: HTMLElement;
  private killFeedEl: HTMLElement;
  private debugEl: HTMLElement;
  private hitMarkerEl: HTMLElement;
  private hudEl: HTMLElement;
  private hitMarkerTimeout: number | null = null;

  // --- Connection quality elements ---
  private connQualityEl: HTMLElement;
  private connIconEl: HTMLElement;
  private connRttEl: HTMLElement;
  private connLossEl: HTMLElement;
  private connTransportEl: HTMLElement;

  // --- Grenade HUD ---
  private grenadeHudEl: HTMLElement;
  private _lastGrenadeKey = '';

  // --- Canvas crosshair ---
  private crosshairRenderer: CrosshairRenderer;

  constructor() {
    const getEl = (id: string, fallbackTag = 'div'): HTMLElement =>
      document.getElementById(id) ?? document.createElement(fallbackTag);

    this.hudEl = getEl('hud');
    this.hudEl.classList.add('hud-container');

    this.healthEl = getEl('health-value', 'span');
    this.armorEl = getEl('armor-value', 'span');
    this.ammoCurrentEl = getEl('ammo-current', 'span');
    this.ammoReserveEl = getEl('ammo-reserve', 'span');
    this.weaponNameEl = getEl('weapon-name', 'span');
    this.timerEl = getEl('timer', 'span');
    this.killFeedEl = getEl('kill-feed');
    this.debugEl = getEl('debug-info');
    this.hitMarkerEl = getEl('hit-marker');

    // Add data-hud-element attributes for mobile CSS targeting
    const healthBar = document.getElementById('health-bar');
    if (healthBar) healthBar.setAttribute('data-hud-element', 'health');

    const ammoDisplay = document.getElementById('ammo-display');
    if (ammoDisplay) ammoDisplay.setAttribute('data-hud-element', 'ammo');

    this.timerEl.setAttribute('data-hud-element', 'timer');

    const minimap = document.getElementById('minimap');
    if (minimap) minimap.setAttribute('data-hud-element', 'minimap');

    this.killFeedEl.setAttribute('data-hud-element', 'killfeed');

    // Create connection quality indicator
    this.connQualityEl = this.createConnectionQualityElement();
    this.connIconEl = this.connQualityEl.querySelector('.conn-icon') ?? document.createElement('span');
    this.connRttEl = this.connQualityEl.querySelector('.conn-rtt') ?? document.createElement('span');
    this.connLossEl = this.connQualityEl.querySelector('.conn-loss') ?? document.createElement('span');
    this.connTransportEl = this.connQualityEl.querySelector('.conn-transport') ?? document.createElement('span');

    // Create grenade inventory HUD
    this.grenadeHudEl = this.createGrenadeHudElement();

    // Canvas-rendered crosshair (replaces CSS .crosshair)
    this.crosshairRenderer = new CrosshairRenderer();
  }

  private createConnectionQualityElement(): HTMLElement {
    const el = document.createElement('div');
    el.id = 'connection-quality';
    el.style.cssText = [
      'position: absolute',
      'top: 12px',
      'right: 200px',
      'display: flex',
      'align-items: center',
      'gap: 6px',
      'font-family: "Courier New", monospace',
      'font-size: 11px',
      'color: #ccc',
      'text-shadow: 1px 1px 2px #000',
      'background: rgba(0, 0, 0, 0.45)',
      'padding: 4px 8px',
      'border-radius: 3px',
      'pointer-events: none',
      'z-index: 11',
    ].join('; ');

    el.innerHTML = [
      '<span class="conn-icon" style="font-size:14px;">&#9679;</span>',
      '<span class="conn-rtt" style="min-width:52px;">-- ms</span>',
      '<span class="conn-loss" style="min-width:36px;color:#888;">0%</span>',
      '<span class="conn-transport" style="color:#666;font-size:10px;">WS</span>',
    ].join('');

    this.hudEl.appendChild(el);
    return el;
  }

  private createGrenadeHudElement(): HTMLElement {
    const el = document.createElement('div');
    el.id = 'grenade-hud';
    el.style.cssText = [
      'position: absolute',
      'bottom: 80px',
      'right: 20px',
      'display: flex',
      'gap: 6px',
      'font-family: "Courier New", monospace',
      'font-size: 11px',
      'pointer-events: none',
      'z-index: 11',
    ].join('; ');

    this.hudEl.appendChild(el);
    return el;
  }

  /**
   * Update the grenade inventory display in the HUD.
   * Shows small color-coded boxes with count for each grenade type carried.
   * Highlights the currently selected grenade.
   */
  updateGrenadeSelection(selectedId: GrenadeId | null, inventory: GrenadeInventory): void {
    const stateKey = JSON.stringify({ selectedId, inventory });
    if (stateKey === this._lastGrenadeKey) return;
    this._lastGrenadeKey = stateKey;

    this.grenadeHudEl.innerHTML = '';

    for (const id of Object.keys(GRENADES) as GrenadeId[]) {
      const count = inventory[id] ?? 0;
      if (count <= 0) continue;

      const isSelected = id === selectedId;
      const color = GRENADE_HUD_COLORS[id] ?? '#888';
      const label = GRENADE_LABELS[id] ?? id.charAt(0).toUpperCase();

      const box = document.createElement('div');
      box.style.cssText = [
        `background: ${isSelected ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.5)'}`,
        `border: 2px solid ${isSelected ? color : 'rgba(255,255,255,0.15)'}`,
        'border-radius: 3px',
        'padding: 4px 6px',
        'text-align: center',
        'min-width: 28px',
        `color: ${color}`,
        'text-shadow: 1px 1px 2px #000',
        isSelected ? 'box-shadow: 0 0 6px ' + color : '',
      ].join('; ');

      box.innerHTML =
        `<div style="font-size:10px;font-weight:bold;letter-spacing:1px;">${label}</div>` +
        `<div style="font-size:12px;font-weight:bold;">${count}</div>`;

      this.grenadeHudEl.appendChild(box);
    }
  }

  updateHealth(health: number): void {
    this.healthEl.textContent = String(Math.max(0, Math.round(health)));
    this.healthEl.style.color = health < 30 ? '#ff4444' : '#fff';
  }

  updateArmor(armor: number): void {
    this.armorEl.textContent = String(Math.max(0, Math.round(armor)));
  }

  updateAmmo(current: number, reserve: number): void {
    this.ammoCurrentEl.textContent = String(current);
    this.ammoReserveEl.textContent = String(reserve);
    this.ammoCurrentEl.style.color = current <= 5 ? '#ff4444' : '#fff';
  }

  updateWeaponName(name: string): void {
    this.weaponNameEl.textContent = name;
  }

  updateTimer(secondsRemaining: number): void {
    const mins = Math.floor(secondsRemaining / 60);
    const secs = secondsRemaining % 60;
    this.timerEl.textContent = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  updateDebugInfo(fps: number, ping: number, tickRate: number): void {
    this.debugEl.textContent = `FPS: ${fps} | Ping: ${ping}ms | Tick: ${tickRate}`;
  }

  /**
   * Update the connection quality indicator in the top-right area.
   * @param quality - Connection quality data from the Connection class.
   * @param interpDelay - Optional interpolation delay to show in debug.
   */
  updateConnectionQuality(quality: ConnectionQuality, interpDelay?: number): void {
    const rtt = Math.round(quality.rtt);
    const lossPercent = Math.round(quality.packetLoss * 100);

    // RTT display
    this.connRttEl.textContent = `${rtt}ms`;

    // Connection quality icon color: green (<50ms), yellow (50-100ms), red (>100ms)
    let iconColor: string;
    if (rtt < 50) {
      iconColor = '#44ff44'; // green
    } else if (rtt < 100) {
      iconColor = '#ffcc00'; // yellow
    } else {
      iconColor = '#ff4444'; // red
    }
    this.connIconEl.style.color = iconColor;

    // RTT text also colored by quality
    this.connRttEl.style.color = iconColor;

    // Packet loss
    this.connLossEl.textContent = `${lossPercent}%`;
    if (lossPercent > 5) {
      this.connLossEl.style.color = '#ff4444';
    } else if (lossPercent > 2) {
      this.connLossEl.style.color = '#ffcc00';
    } else {
      this.connLossEl.style.color = '#888';
    }

    // Transport type
    const transportLabel = quality.transportType === 'webrtc' ? 'RTC' : 'WS';
    const transportColor = quality.transportType === 'webrtc' ? '#44cc88' : '#888';
    this.connTransportEl.textContent = transportLabel;
    this.connTransportEl.style.color = transportColor;
  }

  addKillFeedEntry(killer: string, victim: string, weapon: string, headshot: boolean, noscope = false): void {
    const entry = document.createElement('div');
    entry.style.cssText =
      'padding:4px 8px;background:rgba(0,0,0,0.6);border-radius:3px;font-size:13px;white-space:nowrap;';

    entry.innerHTML = buildKillFeedEntryHTML(killer, victim, weapon, headshot, noscope);

    this.killFeedEl.appendChild(entry);

    // Cap at 5 visible entries
    while (this.killFeedEl.children.length > 5) {
      this.killFeedEl.removeChild(this.killFeedEl.firstChild!);
    }

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (entry.parentNode === this.killFeedEl) {
        this.killFeedEl.removeChild(entry);
      }
    }, 5000);
  }

  /** M1: Add a non-kill event to the kill feed (bomb plant/defuse/explode, timeout, etc.) */
  addEventFeedEntry(icon: string, message: string, color = '#ffcc00'): void {
    const entry = document.createElement('div');
    entry.style.cssText =
      'padding:4px 8px;background:rgba(0,0,0,0.6);border-radius:3px;font-size:13px;white-space:nowrap;';
    entry.innerHTML = `<span style="color:${color};margin-right:4px;">${icon}</span><span style="color:#ccc;">${message}</span>`;
    this.killFeedEl.appendChild(entry);
    while (this.killFeedEl.children.length > 5) {
      this.killFeedEl.removeChild(this.killFeedEl.firstChild!);
    }
    setTimeout(() => {
      if (entry.parentNode === this.killFeedEl) {
        this.killFeedEl.removeChild(entry);
      }
    }, 5000);
  }

  showHitMarker(isHeadshot: boolean): void {
    if (this.hitMarkerTimeout !== null) {
      clearTimeout(this.hitMarkerTimeout);
    }

    const lines = this.hitMarkerEl.querySelectorAll<HTMLElement>('.hm-line');
    const color = isHeadshot ? '#ff4444' : '#fff';
    lines.forEach((line) => {
      line.style.background = color;
    });

    this.hitMarkerEl.style.display = 'block';

    this.hitMarkerTimeout = window.setTimeout(() => {
      this.hitMarkerEl.style.display = 'none';
      this.hitMarkerTimeout = null;
    }, 200);
  }

  show(): void {
    this.hudEl.style.display = 'block';
    this.setSpectatorMode(false);
  }

  hide(): void {
    this.hudEl.style.display = 'none';
  }

  /**
   * Toggle spectator display mode: hides crosshair, weapon/ammo, health/armor
   * while keeping kill feed, timer, round score, and minimap visible.
   */
  setSpectatorMode(spectating: boolean): void {
    const crosshair = this.hudEl.querySelector('.crosshair') as HTMLElement | null;
    const healthBar = document.getElementById('health-bar');
    const ammoDisplay = document.getElementById('ammo-display');
    const weaponName = document.getElementById('weapon-name');
    const moneyDisplay = document.getElementById('money-display');

    const display = spectating ? 'none' : '';
    if (crosshair) crosshair.style.display = display;
    if (healthBar) healthBar.style.display = display;
    if (ammoDisplay) ammoDisplay.style.display = display;
    if (weaponName) weaponName.style.display = display;
    if (moneyDisplay) moneyDisplay.style.display = display;

    // Canvas crosshair
    if (spectating) {
      this.crosshairRenderer.hide();
    } else {
      this.crosshairRenderer.show();
    }
  }

  /**
   * Set the current weapon spread for the dynamic crosshair.
   * Call each frame with the result of getCurrentSpread().
   */
  setCrosshairSpread(spread: number): void {
    this.crosshairRenderer.setSpread(spread);
  }

  /** Render the canvas crosshair. Call once per frame from the game loop. */
  renderCrosshair(): void {
    this.crosshairRenderer.render();
  }

  /** Remove dynamically created DOM elements. */
  dispose(): void {
    this.connQualityEl.remove();
    this.grenadeHudEl.remove();
    this.crosshairRenderer.dispose();
  }
}
